// FileManagerModal.js
import React, { useState, useEffect } from "react";
import {
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiResizableContainer,
  EuiFieldSearch,
  EuiListGroup,
  EuiListGroupItem,
  EuiButton,
  EuiSpacer,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiTextArea,
  EuiFilePicker,
  EuiText,
  EuiSwitch,
  EuiPanel,
} from "@elastic/eui";
import { PostOptions } from "../utils/RequestUtils";
import { getApiUrl } from "../utils/api";
import EditAgentForm from "../forms/EditAgentForm";
import ModeSpecificDocumentSelector from "../components/ModeSpecificDocumentSelector";

const FileManagePage = ({ course, isModalVisible, setIsModalVisible }) => {
  const [files, setFiles] = useState([]);
  const [document, setDocument] = useState(null);
  const [documentUploading, setDocumentUploading] = useState(false);
  const [documentUploadError, setDocumentUploadError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isCourseRelated, setIsCourseRelated] = useState(false);
  const [docDescription, setDocDescription] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [agentName, setAgentName] = useState(null);
  const [agentConfig, setAgentConfig] = useState(null);

  useEffect(() => {
    if (!isModalVisible) {
      return;
    }

    let isMounted = true;
    setAgentName(null);
    setAgentConfig(null);
    setFiles([]);

    fetch(getApiUrl("/api/agent/get/all"), {
      ...PostOptions({ course_name: course.course_name }),
      credentials: "include",
    })
      .then((response) => {
        return response.json().then((text) => {
          if (!response.ok) {
            alert(text.message);
            throw new Error(text.message);
          }
          return text;
        });
      })
      .then((data) => {
        if (!isMounted) {
          return;
        }

        const firstAgent = data?.data?.[0];
        if (firstAgent) {
          setAgentName(firstAgent.config_name);
        } else {
          setAgentName(null);
          setAgentConfig(null);
        }
      })
      .catch((error) => {
        console.error("There was an error!", error);
      });

    return () => {
      isMounted = false;
    };
  }, [course.course_name, isModalVisible]);

  const fetchAgentConfiguration = async (configName) => {
    try {
      const response = await fetch(getApiUrl("/api/agent/get/one"), {
        ...PostOptions({
          config_name: configName,
          course_name: course.course_name,
        }),
        credentials: "include",
      });
      const text = await response.json();
      if (!response.ok) {
        alert(text.message);
        throw new Error(text.message);
      }

      setAgentConfig(text.data);

      let tmp_files = [];
      for (
        let i = 0;
        i < text.data.non_required_inclusion_documents.length;
        i++
      ) {
        tmp_files.push({
          id: tmp_files.length + 1,
          name: text.data.non_required_inclusion_documents[i].name,
          is_related: false,
          description:
            text.data.non_required_inclusion_documents[i].description,
        });
      }
      for (let i = 0; i < text.data.required_inclusion_documents.length; i++) {
        tmp_files.push({
          id: tmp_files.length + 1,
          name: text.data.required_inclusion_documents[i].name,
          is_related: true,
          description: text.data.required_inclusion_documents[i].description,
        });
      }
      setFiles(tmp_files);
    } catch (error) {
      console.error("There was an error!", error);
    }
  };

  useEffect(() => {
    if (agentName) {
      setSelectedFile(null);
      setIsUploading(false);
      fetchAgentConfiguration(agentName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentName]);

  const handleSearchChange = (e) => {
    // Handle search logic here
    setSearchQuery(e.target.value);
  };

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setIsUploading(false);
  };

  const handleDeleteFile = (delFile) => {
    fetch(getApiUrl("/api/documents/remove/one"), {
      ...PostOptions({
        config_name: agentName,
        document_name: delFile.name,
        course_name: course.course_name,
      }),
      credentials: "include",
    })
      .then((response) => {
        return response.json().then((text) => {
          if (!response.ok) {
            alert(text.message);
            throw new Error(text.message);
          }
          return text;
        });
      })
      .then((data) => {
        setFiles(files.filter((file) => file.id !== delFile.id));
        if (selectedFile && selectedFile.id === delFile.id) {
          setSelectedFile(null);
        }
      })
      .catch((error) => {
        console.error("There was an error!", error);
      });
  };

  const handleFileUpload = () => {
    setIsUploading(true);
    setSelectedFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!document || !document.name) {
      setDocumentUploadError("Please select a file to upload.");
      return;
    }
    setDocumentUploadError(null);
    setDocumentUploading(true);
    const formData = new FormData();
    formData.append("file", document);
    formData.append("config_name", agentName);
    formData.append("document_name", document.name);
    formData.append("required_inclusion", isCourseRelated);
    formData.append("doc_description", docDescription);
    formData.append("course_name", course.course_name);

    try {
      const response = await fetch(getApiUrl("/api/documents/upload"), {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      const data = await response.json();
      setDocumentUploading(false);
      if (!response.ok) {
        setDocumentUploadError(data.message);

        // alert the response
        alert(data.message);
        return;
      }
      setFiles([
        ...files,
        {
          id: files.length + 1,
          name: document.name,
          description: docDescription,
          is_related: isCourseRelated,
        },
      ]);
      handleFileSelect({
        id: files.length + 1,
        name: document.name,
        description: docDescription,
        is_related: isCourseRelated,
      });
      setDocument(null);
      setDocDescription("");
      setIsCourseRelated(false);
      setIsUploading(false);
    } catch (error) {
      console.error("There was an error!", error);
      if (error.response && error.response.data) {
        // alert the error if there is one
        alert(error.response.data.message);
      }
    }
  };
  //['config_name', 'document_name', 'required_inclusion', 'doc_description', 'course_name']

  // };

    const onNameChange = (oldName, newName) => {
        setAgentName(newName);
    };

    const onAgentSettingsUpdate = (updates) => {
        setAgentConfig((prev) => (prev ? { ...prev, ...updates } : prev));
    };

  const renderFileList = () => (
    <EuiListGroup bordered flush>
      {files
        .filter(
          (file) =>
            !searchQuery ||
            file.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .map((file, index) => (
          <EuiListGroupItem
            key={index}
            label={file.name}
            iconType={"document"}
            onClick={() => handleFileSelect(file)}
            extraAction={{
              color: "danger",
              onClick: () => handleDeleteFile(file),
              iconType: "trash",
              "aria-label": "Delete document",
              alwaysShow: true,
            }}
          />
        ))}
    </EuiListGroup>
  );

  const onDocumentUpdate = () => {
    setDocumentUploading(true);
    fetch(getApiUrl("/api/documents/update/one"), {
      ...PostOptions({
        config_name: agentName,
        document_name: selectedFile.name,
        required_inclusion: selectedFile.is_related,
        doc_description: selectedFile.description,
        course_name: course.course_name,
      }),
      credentials: "include",
    })
      .then((response) => {
        return response.json().then((text) => {
          if (!response.ok) {
            setDocumentUploadError(text.message);
            alert(text.message);
            throw new Error(text.message);
          }
          return text;
        });
      })
      .then((data) => {
        setDocumentUploading(false);
      })
      .catch((error) => {
        console.error("There was an error!", error);
        setDocumentUploading(false);
      });
  };

  const renderRightPane = () => {
    if (isUploading) {
      return (
        <EuiForm
          component="form"
          onSubmit={handleSubmit}
          error={documentUploadError}
        >
          <EuiFormRow label="Upload File" fullWidth>
            <EuiFilePicker
              fullWidth
              onChange={(files) => {
                setDocument(files.length ? files[0] : null);
              }}
              isLoading={documentUploading}
              disabled={documentUploading}
            />
          </EuiFormRow>
          <EuiFormRow label="File Description" fullWidth>
            <EuiTextArea
              fullWidth
              value={docDescription}
              onChange={(e) => setDocDescription(e.target.value)}
            />
          </EuiFormRow>
          <EuiFormRow
            label="Is this a document related to course information (e.g., syllabus)?"
            fullWidth
            display="columnCompressedSwitch"
          >
            <EuiSwitch
              label={isCourseRelated ? "Yes" : "No"}
              checked={isCourseRelated}
              onChange={(e) => setIsCourseRelated(e.target.checked)}
            />
          </EuiFormRow>
          <EuiText size="s" color="subdued">
            <p>
              Note: These files will be exempt from data controls to ensure
              students receive straightforward, clear responses.
            </p>
          </EuiText>
          <EuiButton
            type="submit"
            fill
            fullWidth
            isLoading={documentUploading}
            disabled={documentUploading}
          >
            Submit
          </EuiButton>
        </EuiForm>
      );
    }

    if (selectedFile) {
      return (
        <EuiForm component="form" onSubmit={onDocumentUpdate}>
          <EuiFormRow label="File Content" fullWidth>
            <EuiTextArea
              value={selectedFile.description}
              fullWidth
              onChange={(e) => {
                setSelectedFile({
                  ...selectedFile,
                  description: e.target.value,
                });
                setFiles(
                  files.map((file) => {
                    if (file.id === selectedFile.id) {
                      return { ...file, description: e.target.value };
                    }
                    return file;
                  })
                );
              }}
            />
          </EuiFormRow>
          <EuiFormRow
            label="Is this a document related to course information (e.g., syllabus)?"
            fullWidth
            display="columnCompressedSwitch"
          >
            <EuiSwitch
              label={isCourseRelated ? "Yes" : "No"}
              checked={selectedFile.is_related}
              onChange={(e) => {
                setSelectedFile({
                  ...selectedFile,
                  is_related: e.target.checked,
                });
                setFiles(
                  files.map((file) => {
                    if (file.id === selectedFile.id) {
                      return { ...file, is_related: e.target.checked };
                    }
                    return file;
                  })
                );
              }}
            />
          </EuiFormRow>
          <EuiButton type="submit" fill fullWidth>
            Update
          </EuiButton>
        </EuiForm>
      );
    }

    return (
      <EuiText>
        <p>Select a file to edit or upload a new file.</p>
      </EuiText>
    );
  };

  return (
    <>
      {isModalVisible && (
        <EuiModal
          onClose={() => setIsModalVisible(false)}
          style={{ minWidth: "50vw", minHeight: "50vh" }}
        >
          <EuiModalHeader>
            <EuiModalHeaderTitle>File Manager</EuiModalHeaderTitle>
          </EuiModalHeader>
          <EuiModalBody>
            <EuiResizableContainer
              direction="horizontal"
              style={{ height: "50vh" }}
            >
              {(EuiResizablePanel, EuiResizableButton) => (
                <>
                  <EuiResizablePanel initialSize={30} minSize="20%">
                    <EuiFlexGroup
                      direction="column"
                      gutterSize="none"
                      style={{ height: "100%" }}
                      justifyContent="spaceBetween"
                    >
                      <EuiFlexItem grow={false}>
                        <EuiFlexGroup
                          direction="column"
                          gutterSize="xs"
                          style={{ height: "100%" }}
                        >
                          <EuiFlexItem grow={false}>
                            <EuiPanel paddingSize="s">
                              <EuiText size="s">
                                <strong>Agent:</strong>{" "}
                                {agentName ||
                                  "No agent configured for this course"}
                              </EuiText>
                            </EuiPanel>
                          </EuiFlexItem>
                          <EuiFlexItem grow={false}>
                            {agentConfig && (
                              <EditAgentForm
                                config={agentConfig}
                                course={course}
                                onNameChange={onNameChange}
                                onSettingsUpdate={onAgentSettingsUpdate}
                              />
                            )}
                          </EuiFlexItem>
                          <EuiFlexItem grow={false}>
                            <EuiFieldSearch
                              compressed
                              placeholder="Search files"
                              fullWidth
                              onChange={handleSearchChange}
                            />
                            <EuiSpacer size="m" />
                          </EuiFlexItem>
                          <EuiFlexItem
                            grow={false}
                            style={{ overflowY: "auto" }}
                          >
                            {renderFileList()}
                          </EuiFlexItem>
                        </EuiFlexGroup>
                      </EuiFlexItem>
                      <EuiFlexItem grow={false}>
                        <EuiSpacer size="m" />
                        <EuiButton fullWidth onClick={handleFileUpload}>
                          Upload File
                        </EuiButton>
                      </EuiFlexItem>
                    </EuiFlexGroup>
                  </EuiResizablePanel>
                  <EuiResizableButton />
                  <EuiResizablePanel initialSize={70} minSize="50%">
                    <>
                      {renderRightPane()}
                      {agentConfig && (
                        <>
                          <EuiSpacer size="xl" />
                          <ModeSpecificDocumentSelector
                            course={course}
                            agentConfig={agentConfig}
                            onConfigUpdate={() =>
                              fetchAgentConfiguration(agentConfig.config_name)
                            }
                          />
                        </>
                      )}
                    </>
                  </EuiResizablePanel>
                </>
              )}
            </EuiResizableContainer>
          </EuiModalBody>
        </EuiModal>
      )}
    </>
  );
};

export default FileManagePage;
